<template>
  <div class="points-mall-page page-wrapper">
    <el-card shadow="hover">
      <el-tabs v-model="activeTab" @tab-click="onTabClick">
        <el-tab-pane label="积分商城商品" name="goods">
          <div class="toolbar">
            <el-button type="primary" size="small" class="btn-primary" @click="openGoodsDialog(null)">
              上架商品
            </el-button>
            <span class="toolbar-hint">从「商品管理」中的商品选择，并设置兑换所需积分；同一商品仅可上架一条。</span>
          </div>
          <el-table :data="goodsList" border style="width: 100%" v-loading="goodsLoading">
            <el-table-column prop="product_name" label="商品名称" min-width="160" />
            <el-table-column prop="product_id" label="商品ID" min-width="200" show-overflow-tooltip />
            <el-table-column prop="price" label="售价(元)" width="100" />
            <el-table-column prop="points_needed" label="兑换积分" width="100" />
            <el-table-column label="类型" width="120">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.goods_type === 'voucher'" type="warning" size="small">兑换券</el-tag>
                <el-tag v-else size="small">实物/其他</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="voucher_value" label="券面额(元)" width="110">
              <template slot-scope="scope">
                {{ scope.row.goods_type === 'voucher' && scope.row.voucher_value != null ? scope.row.voucher_value : '—' }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
                  {{ scope.row.status === 1 ? '上架' : '下架' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="sort_order" label="排序" width="80" />
            <el-table-column label="操作" width="180" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" class="btn-secondary" @click="openGoodsDialog(scope.row)">编辑</el-button>
                <el-button
                  v-if="canAdminDeleteSession"
                  size="mini"
                  class="btn-secondary danger"
                  @click="removeGoods(scope.row)"
                >删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="赠送积分" name="gift">
          <el-form :model="giftForm" label-width="100px" class="gift-form" style="max-width: 520px">
            <el-form-item label="客户" required>
              <el-select
                v-model="giftForm.user_id"
                filterable
                placeholder="选择或搜索客户（姓名/手机/ID）"
                style="width: 100%"
              >
                <el-option
                  v-for="u in userOptions"
                  :key="u.id"
                  :label="giftUserLabel(u)"
                  :value="u.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="赠送积分" required>
              <el-input v-model.number="giftForm.amount" type="number" min="1" placeholder="正整数" />
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="giftForm.reason" type="textarea" :rows="2" placeholder="选填，会记入客户积分流水" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" class="btn-primary" :loading="giftSubmitting" @click="submitGift">
                确认赠送
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="积分兑换日志" name="log">
          <div class="log-toolbar">
            <el-input
              v-model="logKeyword"
              placeholder="搜索用户ID或商品名称"
              clearable
              style="width: 280px"
              @keyup.enter.native="loadExchangeLog(1)"
            />
            <el-button type="primary" size="small" class="btn-primary" @click="loadExchangeLog(1)">查询</el-button>
          </div>
          <el-table :data="exchangeLog" border style="width: 100%" v-loading="logLoading">
            <el-table-column prop="user_id" label="账号ID" min-width="200" show-overflow-tooltip />
            <el-table-column prop="customer_name" label="顾客" width="120" />
            <el-table-column prop="operator" label="操作员" min-width="140" show-overflow-tooltip />
            <el-table-column prop="points_cost" label="消耗积分" width="100" />
            <el-table-column prop="product_name" label="兑换商品" min-width="160" show-overflow-tooltip />
            <el-table-column prop="created_at" label="兑换时间" min-width="160">
              <template slot-scope="scope">
                {{ $formatDateTime(scope.row.created_at) }}
              </template>
            </el-table-column>
          </el-table>
          <admin-pagination
            :total="logTotal"
            :page.sync="logPage"
            :page-size.sync="logPageSize"
            @change="loadExchangeLog"
          />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-dialog
      :title="goodsDialogTitle"
      :visible.sync="goodsDialogVisible"
      width="520px"
      append-to-body
      destroy-on-close
    >
      <el-form :model="goodsForm" label-width="110px">
        <el-form-item label="选择商品" required>
          <el-select
            v-model="goodsForm.product_id"
            filterable
            placeholder="从商品管理中选择"
            style="width: 100%"
            :disabled="!!goodsForm.id"
          >
            <el-option
              v-for="p in productSelectOptions"
              :key="p.id"
              :label="`${p.name}（¥${p.price}）`"
              :value="String(p.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="兑换所需积分" required>
          <el-input v-model.number="goodsForm.points_needed" type="number" min="1" placeholder="正整数" />
        </el-form-item>
        <el-form-item label="商品类型">
          <el-radio-group v-model="goodsForm.goods_type">
            <el-radio label="product">普通商品</el-radio>
            <el-radio label="voucher">兑换券/红包</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="goodsForm.goods_type === 'voucher'" label="券面额(元)" required>
          <el-input v-model.number="goodsForm.voucher_value" type="number" min="0.01" step="0.01" placeholder="下单时可抵扣金额" />
          <div class="form-tip">用户兑换后将自动发放至「我的兑换券」</div>
        </el-form-item>
        <el-form-item label="排序">
          <el-input v-model.number="goodsForm.sort_order" type="number" placeholder="数字越小越靠前" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="goodsForm.status">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="goodsDialogVisible = false">取消</el-button>
        <el-button type="primary" class="btn-primary" :loading="goodsSaving" @click="saveGoods">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fetchProducts } from '../api/products'
import { formatApiError } from '../utils/apiError'
import { canAdminDelete } from '../utils/auth'
import { routeTabMixin } from '../utils/routeTab'

export default {
  name: 'PointsMall',
  mixins: [
    routeTabMixin({
      allowed: ['goods', 'gift', 'log']
    })
  ],
  data() {
    return {
      activeTab: 'goods',
      goodsList: [],
      goodsLoading: false,
      goodsDialogVisible: false,
      goodsSaving: false,
      goodsDialogTitle: '上架积分商品',
      goodsForm: {
        id: null,
        product_id: '',
        points_needed: 100,
        sort_order: 0,
        status: 1,
        goods_type: 'product',
        voucher_value: 10
      },
      allProducts: [],
      userOptions: [],
      giftForm: {
        user_id: '',
        amount: 100,
        reason: ''
      },
      giftSubmitting: false,
      exchangeLog: [],
      logLoading: false,
      logKeyword: '',
      logPage: 1,
      logPageSize: 30,
      logTotal: 0
    }
  },
  computed: {
    canAdminDeleteSession() {
      return canAdminDelete()
    },
    productSelectOptions() {
      const listed = new Set((this.goodsList || []).map((g) => String(g.product_id)))
      if (this.goodsForm.id) {
        return this.allProducts
      }
      return (this.allProducts || []).filter((p) => !listed.has(String(p.id)))
    }
  },
  mounted() {
    this.loadGoods()
    this.loadProducts()
  },
  methods: {
    giftUserLabel(u) {
      const name = (u && u.username) || '—'
      const phone = (u && u.phone) || ''
      const id = (u && u.id) || ''
      return `${name} ${phone ? phone + ' ' : ''}(${id})`
    },
    onTabClick(tab) {
      if (tab.name === 'gift') {
        this.loadGiftCustomerOptions()
      }
      if (tab.name === 'log' && !this.exchangeLog.length) {
        this.loadExchangeLog(1)
      }
    },
    onRouteTabRestored(name) {
      this.onTabClick({ name })
    },
    async loadProducts() {
      try {
        const resp = await fetchProducts()
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.allProducts = resp.data
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载商品列表失败'))
      }
    },
    async loadGoods() {
      this.goodsLoading = true
      try {
        const resp = await this.$api.get('/admin/points-goods')
        if (resp && resp.code === 200) {
          this.goodsList = Array.isArray(resp.data) ? resp.data : []
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载积分商品失败'))
      } finally {
        this.goodsLoading = false
      }
    },
    /**
     * 与「客户与用户等级 → 用户等级管理」一致：合并 /admin/user-level-list 与 /admin/users
     * （含仅有 eating_users、尚无 user_level_points 的「未分级」客户，以及仅有等级流水的账号）
     */
    async loadGiftCustomerOptions() {
      try {
        const [levelResp, usersResp] = await Promise.all([
          this.$api.get('/admin/user-level-list'),
          this.$api.get('/admin/users')
        ])
        const levelRows =
          levelResp && levelResp.code === 200 && Array.isArray(levelResp.data) ? levelResp.data : []
        const customerRows =
          usersResp && usersResp.code === 200 && Array.isArray(usersResp.data) ? usersResp.data : []

        const byId = new Map()
        levelRows.forEach((r) => {
          const uid = String((r && r.user_id) || '').trim()
          if (!uid) return
          byId.set(uid, {
            id: uid,
            username: (r && r.nickname) || '',
            phone: (r && r.phone) || ''
          })
        })
        customerRows.forEach((cu) => {
          const uid = String((cu && cu.id) || '').trim()
          if (!uid || byId.has(uid)) return
          byId.set(uid, {
            id: uid,
            username: (cu && cu.username) || '',
            phone: (cu && cu.phone) || ''
          })
        })

        this.userOptions = Array.from(byId.values()).sort((a, b) => {
          const sa = `${a.username || ''}${a.phone || ''}${a.id}`
          const sb = `${b.username || ''}${b.phone || ''}${b.id}`
          return sa.localeCompare(sb, 'zh')
        })
      } catch (e) {
        this.userOptions = []
        this.$message.error(formatApiError(e, '加载客户列表失败'))
      }
    },
    openGoodsDialog(row) {
      if (row) {
        this.goodsDialogTitle = '编辑积分商品'
        this.goodsForm = {
          id: row.id,
          product_id: String(row.product_id),
          points_needed: Number(row.points_needed) || 100,
          sort_order: Number(row.sort_order) || 0,
          status: row.status === 0 ? 0 : 1,
          goods_type: row.goods_type === 'voucher' ? 'voucher' : 'product',
          voucher_value: row.voucher_value != null ? Number(row.voucher_value) : 10
        }
      } else {
        this.goodsDialogTitle = '上架积分商品'
        this.goodsForm = {
          id: null,
          product_id: '',
          points_needed: 100,
          sort_order: 0,
          status: 1,
          goods_type: 'product',
          voucher_value: 10
        }
      }
      this.goodsDialogVisible = true
    },
    async saveGoods() {
      const pid = String(this.goodsForm.product_id || '').trim()
      const pts = Math.max(1, Math.floor(Number(this.goodsForm.points_needed)))
      if (!pid) {
        this.$message.warning('请选择商品')
        return
      }
      if (this.goodsForm.goods_type === 'voucher') {
        const vv = Number(this.goodsForm.voucher_value)
        if (!Number.isFinite(vv) || vv <= 0) {
          this.$message.warning('请填写兑换券面额')
          return
        }
      }
      this.goodsSaving = true
      try {
        const body = {
          id: this.goodsForm.id || undefined,
          product_id: pid,
          points_needed: pts,
          sort_order: Math.floor(Number(this.goodsForm.sort_order)) || 0,
          status: this.goodsForm.status === 0 ? 0 : 1,
          goods_type: this.goodsForm.goods_type === 'voucher' ? 'voucher' : 'product',
          voucher_value:
            this.goodsForm.goods_type === 'voucher'
              ? Number(this.goodsForm.voucher_value)
              : null
        }
        let resp
        if (this.goodsForm.id) {
          resp = await this.$api.put(`/admin/points-goods/${this.goodsForm.id}`, body)
        } else {
          resp = await this.$api.post('/admin/points-goods', body)
        }
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '保存成功')
          this.goodsDialogVisible = false
          await this.loadGoods()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.goodsSaving = false
      }
    },
    removeGoods(row) {
      this.$confirm(`确定从积分商城移除「${row.product_name}」吗？`, '提示', {
        type: 'warning'
      })
        .then(async () => {
          try {
            const resp = await this.$api.delete(`/admin/points-goods/${row.id}`)
            if (resp && resp.code === 202) {
              this.$message.warning((resp && resp.message) || '删除申请已提交')
            } else if (resp && resp.code === 200) {
              this.$message.success((resp && resp.message) || '已删除')
              await this.loadGoods()
            } else {
              this.$message.error((resp && resp.message) || '删除失败')
            }
          } catch (e) {
            this.$message.error(formatApiError(e, '删除失败'))
          }
        })
        .catch(() => {})
    },
    async submitGift() {
      const uid = String(this.giftForm.user_id || '').trim()
      const amt = Math.floor(Number(this.giftForm.amount))
      if (!uid) {
        this.$message.warning('请选择客户')
        return
      }
      if (!amt || amt < 1) {
        this.$message.warning('赠送积分须为正整数')
        return
      }
      this.giftSubmitting = true
      try {
        const resp = await this.$api.post('/admin/points-gift', {
          user_id: uid,
          amount: amt,
          reason: String(this.giftForm.reason || '').trim()
        })
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '赠送成功')
          this.giftForm.reason = ''
        } else {
          this.$message.error((resp && resp.message) || '赠送失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '赠送失败'))
      } finally {
        this.giftSubmitting = false
      }
    },
    async loadExchangeLog(payload) {
      const p =
        payload && typeof payload === 'object' && payload.page != null
          ? payload.page
          : payload != null
            ? payload
            : this.logPage
      if (payload && typeof payload === 'object' && payload.pageSize != null) {
        this.logPageSize = payload.pageSize
      }
      this.logPage = p
      this.logLoading = true
      try {
        const resp = await this.$api.get('/admin/points-exchange-log', {
          params: {
            page: p,
            pageSize: this.logPageSize,
            keyword: this.logKeyword.trim()
          }
        })
        if (resp && resp.code === 200 && resp.data) {
          this.exchangeLog = Array.isArray(resp.data.list) ? resp.data.list : []
          this.logTotal = Number(resp.data.total) || 0
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载兑换日志失败'))
      } finally {
        this.logLoading = false
      }
    }
  }
}
</script>

<style scoped>
.points-mall-page {
  padding: 24px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.toolbar-hint {
  font-size: 13px;
  color: #909399;
}
.log-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.log-pager {
  margin-top: 16px;
  text-align: right;
}
.gift-form {
  margin-top: 8px;
}
.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
